import { Uuid } from "@/domains/global/identifiers";
import { resolveNoteAttachmentUuid } from "@/store/note-attachments/resolveAttachmentUuid";
import { NoteAttachmentHostingStatus } from "@/store/note-attachments/types";
import { isProcessableAttachmentUrl } from "@/store/note-attachments/url-utils";
import { BatchCreateSpaceAccountNoteAttachmentItem } from "@/store/sync/operations/types";

const PDF_PAGE_COUNT_EVICTION_MS = 60_000;

/**
 * Per-note glue for the attachment-create path. It no longer dispatches the
 * BatchCreate itself — each upload op owns its create (so the create →
 * hosting-status lifecycle is one ordered sequence on one queue). The submitter
 * now provides the two pieces of create glue that don't belong in either the
 * editor store or the ops:
 *
 *  - the PDF page-count bridge: `recordPdfPageCount` (called from
 *    `validateFileBeforeInsert`) stashes the count keyed by fileId; the
 *    `FileAdded` handler consumes it and threads it onto the file upload op so
 *    the created row's `page_count` / PDF-page quota stay accurate.
 *  - `buildCreateItem`: the pure builder the upload ops use to construct the
 *    `BatchCreate` item (URL guard + deterministic id), so the two ops don't
 *    duplicate it.
 */
export class NoteAttachmentSubmitter {
  private readonly pdfPageCounts = new PdfPageCountStore();

  recordPdfPageCount({ fileId, pageCount }: { fileId: string; pageCount: number }) {
    this.pdfPageCounts.set(fileId.toLowerCase(), pageCount);
  }

  consumePdfPageCount(fileId: string): number | null {
    return this.pdfPageCounts.consume(fileId.toLowerCase());
  }

  /**
   * Build the optimistic-create item for an inserted attachment, or null when
   * the URL isn't a processable attachment URL (matches the legacy guard).
   * Static + pure so the upload ops (which hold `noteId` but not the per-note
   * submitter instance) can share it. `hostingStatus` is set for direct
   * uploads; omitted for payloads that don't carry one. `pageCount` feeds the
   * PDF stub / quota; images pass null (the create hardcodes a 1-page image
   * stub).
   */
  static buildCreateItem({
    noteId,
    downloadUrl,
    mimeType,
    filename,
    pageCount,
    hostingStatus,
  }: {
    noteId: Uuid;
    downloadUrl: string | undefined;
    mimeType: string;
    filename: string;
    pageCount: number | null;
    hostingStatus?: NoteAttachmentHostingStatus | null;
  }): BatchCreateSpaceAccountNoteAttachmentItem | null {
    if (!downloadUrl || !isProcessableAttachmentUrl(downloadUrl)) return null;

    return {
      associated_note_attachment_id: resolveNoteAttachmentUuid(noteId, downloadUrl),
      associated_note_id: noteId,
      attachment_url: downloadUrl,
      mime_type: mimeType,
      filename,
      page_count: pageCount,
      ...(hostingStatus !== undefined ? { hosting_status: hostingStatus } : {}),
    };
  }

  dispose() {
    this.pdfPageCounts.clear();
  }
}

/**
 * Short-lived map of fileId → pdfPageCount produced by
 * `validateFileBeforeInsert` and consumed by `FileAdded`. Entries self-evict
 * after 60s — the FileAdded event usually arrives within microseconds, but a
 * rejected upload would otherwise orphan one.
 */
class PdfPageCountStore {
  private readonly counts = new Map<string, number>();
  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();

  set(key: string, pageCount: number) {
    this.counts.set(key, pageCount);
    const existing = this.timers.get(key);
    if (existing) clearTimeout(existing);
    this.timers.set(
      key,
      setTimeout(() => this.evict(key), PDF_PAGE_COUNT_EVICTION_MS)
    );
  }

  consume(key: string): number | null {
    const value = this.counts.get(key);
    if (value === undefined) return null;
    this.evict(key);
    return value;
  }

  clear() {
    for (const timer of this.timers.values()) clearTimeout(timer);
    this.timers.clear();
    this.counts.clear();
  }

  private evict(key: string) {
    this.counts.delete(key);
    const timer = this.timers.get(key);
    if (timer) clearTimeout(timer);
    this.timers.delete(key);
  }
}
